import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
// material
import {
  Card,
  Stack,
  Container,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  IconButton,
  Typography,
  Dialog,
  DialogContent
} from '@mui/material';
import { Icon } from '@iconify/react';
import arrowCircleRightOutline from '@iconify/icons-eva/arrow-circle-right-outline';
import closeOutline from '@iconify/icons-eva/close-outline';
import { grey } from '@mui/material/colors';
import { styled } from '@mui/material/styles';
// contexts
// contexts
import TicketsProvider from '../../contexts/Tickets';
import { EventContext } from '../../contexts/Event';
// components
import Page from '../../components/Page';
import EventFormBasic from '../../components/event/form/basic';
import EventFormDate from '../../components/event/form/calendar';
import EventFormLocation from '../../components/event/form/location';
import EventFormInfo from '../../components/event/form/info';
import EventFormSeatsIO from '../../components/event/form/seatsio';
import EventFormTheme from '../../components/event/form/theme';
import SectionList from '../../components/section/list';

// ----------------------------------------------------------------------

const CardStyle = styled(Card)(({ theme }) => ({
  boxShadow: 'none',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    border: `1px dashed ${grey[300]}`
  }
}));

// TODO: mudar para swipe
export default function EventCreate() {
  const { event } = useContext(EventContext);

  const [activeStep, setActiveStep] = useState(0);
  const [bookingType, setBookingType] = useState();
  const [horizontalActiveStep, setHorizontalActiveStep] = useState(0);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('eventId')) {
      setHorizontalActiveStep(1);
    }
  }, [searchParams]);

  const handleNext = () => {
    setActiveStep((nextActiveStep) => nextActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleHorizontalNext = () => {
    setHorizontalActiveStep((nextHorizontalActiveStep) => nextHorizontalActiveStep + 1);
  };

  const handleHorizontalBack = () => {
    setHorizontalActiveStep((prevHorizontalActiveStep) => prevHorizontalActiveStep - 1);
  };

  const handleBookingType = (type) => {
    setBookingType(type);
    setSearchParams({ bookingType: type });
    setHorizontalActiveStep((nextHorizontalActiveStep) => nextHorizontalActiveStep + 1);
  };

  const handleClose = () => {
    if (searchParams.get('eventId')) {
      window.location.href = `/event/edit?eventId=${searchParams.get('eventId')}`;
    } else {
      window.location.href = `/event/list`;
    }
  };

  const handleFinish = () => {
    window.location.href = `/dashboard/sales?eventId=${searchParams.get('eventId')}`;
  };

  return (
    <TicketsProvider>
      <Page title="Event" path="/event/create">
        <Container>
          <Dialog open fullWidth scroll="body" maxWidth="lg" sx={{ m: -2 }}>
            <DialogContent sx={{ px: { xs: 1, sm: 3 } }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: { xs: 1, sm: 3 } }}
              >
                <Typography variant="h4" gutterBottom>
                  Create Event
                </Typography>
                <IconButton size="large" onClick={handleClose}>
                  <Icon icon={closeOutline} />
                </IconButton>
              </Stack>

              {horizontalActiveStep === 0 && (
                <>
                  <CardStyle sx={{ mt: 3 }}>
                    <Box sx={{ py: 10 }}>
                      <Box sx={{ textAlign: 'center', px: 2 }}>
                        <Typography variant="h5" sx={{ mb: 2 }}>
                          Choose Your Booking Type
                          <Typography
                            component="span"
                            variant="body2"
                            color="grey.500"
                            sx={{ display: 'block' }}
                          >
                            If you choose SEATS, talk to our support to get your KEYS
                          </Typography>
                        </Typography>

                        <Stack
                          spacing={2}
                          direction="row"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Button
                            size="large"
                            variant="outlined"
                            onClick={() => handleBookingType('VOY')}
                          >
                            Normal
                          </Button>
                          <Typography
                            component="span"
                            variant="body2"
                            color="grey.500"
                            sx={{ display: 'block', mt: 2 }}
                          >
                            or
                          </Typography>
                          <Button
                            size="large"
                            variant="outlined"
                            onClick={() => handleBookingType('SEATSIO')}
                            disabled
                          >
                            Seats
                          </Button>
                        </Stack>
                      </Box>
                    </Box>
                  </CardStyle>
                </>
              )}

              {horizontalActiveStep === 1 && (
                <>
                  <CardStyle sx={{ mt: { xs: 1, sm: 3 } }}>
                    <Box sx={{ p: { xs: 1, sm: 3 } }}>
                      <Box mb={3}>
                        <Typography variant="h4">Event</Typography>
                      </Box>
                      <Stepper activeStep={activeStep} orientation="vertical">
                        <Step>
                          <StepLabel>Basic Info</StepLabel>
                          <StepContent TransitionProps={{ unmountOnExit: false }}>
                            <Box sx={{ pt: 2 }}>
                              <EventFormBasic event={event} handleNext={handleNext} />
                            </Box>
                          </StepContent>
                        </Step>

                        <Step>
                          <StepLabel>Schedule</StepLabel>
                          <StepContent TransitionProps={{ unmountOnExit: false }}>
                            <Box sx={{ pt: 2 }}>
                              <EventFormDate
                                event={event}
                                handleBack={handleBack}
                                handleNext={handleNext}
                              />
                            </Box>
                          </StepContent>
                        </Step>

                        <Step>
                          <StepLabel>Event Location</StepLabel>
                          <StepContent TransitionProps={{ unmountOnExit: false }}>
                            <Box sx={{ pt: 2 }}>
                              <EventFormLocation
                                event={event}
                                handleBack={handleBack}
                                handleNext={handleNext}
                              />
                            </Box>
                          </StepContent>
                        </Step>

                        <Step>
                          <StepLabel>
                            Add Additional Info
                            <Typography component="span" variant="body2" color="grey.500" ml={1}>
                              (Optional)
                            </Typography>
                          </StepLabel>
                          <StepContent TransitionProps={{ unmountOnExit: false }}>
                            <Box sx={{ pt: 2 }}>
                              <EventFormInfo
                                event={event}
                                handleNext={handleNext}
                                handleBack={handleBack}
                              />
                            </Box>
                          </StepContent>
                        </Step>

                        {(bookingType === 'SEATSIO' || event?.bookingType === 'SEATSIO') && (
                          <Step>
                            <StepLabel>Seats Settings</StepLabel>
                            <StepContent TransitionProps={{ unmountOnExit: false }}>
                              <Box sx={{ pt: 2 }}>
                                <EventFormSeatsIO
                                  event={event}
                                  handleBack={handleBack}
                                  handleNext={handleNext}
                                />
                              </Box>
                            </StepContent>
                          </Step>
                        )}

                        <Step>
                          <StepLabel
                            optional={
                              <Typography variant="caption">Color, Logo, Banner</Typography>
                            }
                          >
                            Choose Your Theme
                          </StepLabel>
                          <StepContent TransitionProps={{ unmountOnExit: false }}>
                            <Box sx={{ pt: 2 }}>
                              <EventFormTheme event={event} handleBack={handleBack} />
                            </Box>
                          </StepContent>
                        </Step>
                      </Stepper>
                    </Box>
                  </CardStyle>
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    {!event?.bookingType && (
                      <Button
                        size="large"
                        variant="outlined"
                        onClick={handleHorizontalBack}
                        sx={{ mr: 1 }}
                      >
                        Back
                      </Button>
                    )}
                    <Button
                      size="large"
                      variant="contained"
                      onClick={handleHorizontalNext}
                      endIcon={<Icon icon={arrowCircleRightOutline} />}
                      disabled={!event?._id}
                    >
                      Next
                    </Button>
                  </Box>
                </>
              )}

              {horizontalActiveStep === 2 && (
                <>
                  <CardStyle sx={{ mt: { xs: 1, sm: 3 } }}>
                    <Box sx={{ p: { xs: 1, sm: 3 }, mx: -2 }}>
                      <SectionList />
                    </Box>
                  </CardStyle>
                  <Box sx={{ p: { xs: 1, sm: 3 }, textAlign: 'center' }}>
                    <Button
                      size="large"
                      variant="outlined"
                      onClick={handleHorizontalBack}
                      sx={{ mr: 1 }}
                    >
                      Back
                    </Button>
                    <Button size="large" variant="contained" onClick={handleFinish}>
                      Finish
                    </Button>
                  </Box>
                </>
              )}
            </DialogContent>
          </Dialog>
        </Container>
      </Page>
    </TicketsProvider>
  );
}
